import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock } from "lucide-react";

const V001_STORY = [
  "THE FIR$T I$$UE",
  "EVERY STORY STARTS SOMEWHERE.",
  "OURS STARTED WITH THE PARTS PEOPLE DON'T TALK ABOUT.",
  "OPENING SOON",
];

const VAULTS = [
  { vault: "001", title: "THE FIR$T I$$UE", preview: true, story: V001_STORY },
  ...["002", "003", "004", "005", "006", "007", "008", "009", "010"].map((vault) => ({
    vault,
    title: "SEALED",
    preview: false,
  })),
];

export default function VaultRegistry() {
  const [open, setOpen] = useState(null);

  return (
    <section id="vaults" data-testid="vault-registry-section" className="relative border-t border-zinc-800/60 concrete-grain">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-28 sm:py-36">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          className="mb-4"
        >
          <p className="text-[10px] tracking-[0.45em] text-zinc-600 uppercase">Comfort Kills Kings</p>
          <p className="text-[10px] tracking-[0.45em] text-zinc-600 uppercase mt-1.5">CXMXII Worldwide</p>
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-5xl sm:text-7xl chrome-text uppercase mb-4"
        >
          Ghetto Diaries Series
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-xs tracking-[0.5em] text-red-300/80 uppercase mb-16"
          data-testid="vault-opening-soon"
        >
          O P E N I N G S O O N
        </motion.p>

        <div className="flex flex-col">
          {VAULTS.map((v, i) => {
            const id = `V${v.vault}`;
            const isOpen = open === id;
            return (
              <motion.div
                key={v.vault}
                data-testid={`vault-entry-${id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className={`border-t border-zinc-800/70 ${i === VAULTS.length - 1 ? "border-b" : ""}`}
              >
                <div
                  className={`group grid grid-cols-[auto_1fr] md:grid-cols-[110px_1fr_1.4fr_auto] gap-3 md:gap-8 items-baseline px-2 sm:px-4 transition-colors duration-500 ${
                    v.preview ? "py-10 hover:bg-red-950/10" : "py-6 hover:bg-zinc-900/30"
                  }`}
                >
                  <span className="text-[10px] tracking-[0.35em] text-zinc-600 uppercase">
                    Vault {v.vault}
                  </span>
                  <span
                    className={`font-display uppercase leading-none transition-transform duration-500 group-hover:-translate-y-0.5 ${
                      v.preview ? "text-3xl sm:text-5xl chrome-text-bright" : "text-2xl sm:text-4xl outline-text"
                    }`}
                  >
                    {v.title}
                  </span>
                  <span className="hidden md:block" />
                  <span className="col-span-2 md:col-span-1 flex items-center gap-3 md:justify-end flex-wrap">
                    {v.preview ? (
                      <>
                        <button
                          data-testid={`vault-shop-${id}`}
                          onClick={() => document.querySelector("#shop")?.scrollIntoView({ behavior: "smooth" })}
                          className="inline-flex items-center gap-2 border border-red-900/70 bg-red-950/30 px-4 py-2 text-[9px] tracking-[0.35em] text-red-200 uppercase hover:bg-red-900/40 transition-colors duration-300"
                        >
                          Preview — In the Armoury
                        </button>
                        <button
                          data-testid={`vault-story-toggle-${id}`}
                          onClick={() => setOpen(isOpen ? null : id)}
                          className={`inline-flex items-center border px-4 py-2 text-[9px] tracking-[0.35em] uppercase transition-colors duration-300 ${
                            isOpen
                              ? "border-red-900/70 bg-red-950/30 text-red-200"
                              : "border-zinc-700/70 text-zinc-500 hover:border-zinc-400 hover:text-zinc-200"
                          }`}
                        >
                          {isOpen ? "Close Story" : "Read Story"}
                        </button>
                      </>
                    ) : (
                      <span
                        data-testid={`vault-sealed-${id}`}
                        className="inline-flex items-center gap-2 text-[9px] tracking-[0.35em] text-zinc-600 uppercase"
                      >
                        <Lock className="h-3.5 w-3.5 text-zinc-700" />
                        Sealed
                      </span>
                    )}
                  </span>
                </div>
                {v.preview && (
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 sm:px-6 pb-12 md:pl-[150px]" data-testid={`vault-story-${id}`}>
                          <p className="max-w-2xl border-l-2 border-red-900/70 pl-6 text-xs sm:text-sm leading-loose text-zinc-400">
                            {v.story.map((line, li) => (
                              <span
                                key={li}
                                className={`block ${
                                  li === 0
                                    ? "text-zinc-100 font-bold tracking-[0.2em]"
                                    : li === v.story.length - 1
                                      ? "text-red-300/90 tracking-[0.4em] mt-3"
                                      : ""
                                }`}
                              >
                                {line}
                              </span>
                            ))}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
